import { useEffect, useState } from 'react'
import { fetchAge, type AgeResponse } from '../../lib/api'
import { useFlowFilter } from '../FlowFilterContext'

interface Props { range: { from: number; to: number } }

const fmt = (n: number) => n.toLocaleString('da-DK')

export function AgeDistribution({ range }: Props) {
  const { variantParam, audienceParam } = useFlowFilter()
  const [data, setData] = useState<AgeResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    fetchAge(range, variantParam, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, variantParam, audienceParam])

  if (error) return <div className="card card-empty">Kunne ikke hente alders-data</div>
  if (!data) return <div className="card card-skeleton" aria-busy />
  if (data.total === 0) return (
    <div className="card card-empty"><div className="card-title">Aldersfordeling</div><div className="muted">Ingen leads i perioden</div></div>
  )

  const max = Math.max(1, ...data.buckets.map((b) => b.n))
  const top = [...data.buckets].sort((a, b) => b.n - a.n).find((b) => b.band !== 'unknown')

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Aldersfordeling</div>
        <div className="card-subtitle">Leads pr. aldersgruppe</div>
      </div>
      {top && top.n > 0 && (
        <div className="card-note small">
          Største segment: <strong>{top.band}</strong> · {fmt(top.n)} leads ({((top.n / data.total) * 100).toFixed(0)}%)
        </div>
      )}
      <div className="age-bars">
        {data.buckets.map((b) => {
          const pct = b.n / max
          const sharePct = data.total > 0 ? (b.n / data.total) * 100 : 0
          return (
            <div key={b.band} className="age-bar">
              <div className="age-bar-track">
                <div className="age-bar-fill" style={{ height: `${pct * 100}%` }} />
              </div>
              <div className="age-bar-count">{fmt(b.n)}</div>
              <div className="age-bar-share muted">{sharePct.toFixed(0)}%</div>
              <div className="age-bar-label">{b.band}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
